import {admin, dashboards, features, help, site} from './views';

import containers from './containers';
import { useUiStore } from '@/stores/ui';

/**
 * Common structure for defining Routes and Menu
 *
 * label => Menu name
 * name => Route name (defaults to label)
 * path => Path fragment (built to url for menu)
 *
 * Any entry without a label will be omitted from the menu.
 */

const items = [
  {
    path: '',
    name: 'home',
    redirect: { name: 'Summary' },
    header: false,
  },
  {
    path: 'client-selection',
    url: 'client-selection',
    component: site.ClientSelection,
    header: false,
    meta: { showMenu: false },
  },
  {
    path: 'terms',
    url: 'terms',
    component: site.Terms,
    header: false,
    meta: { showMenu: false },
  },
  {
    path: 'no-entities',
    url: 'no-entities',
    name: 'Welcome to Travel Analytics',
    component: site.NoEntities,
    header: false,
    meta: { showMenu: false },
  },
  // Note: /logout is intentionally NOT registered here. main.ts short
  // circuits before getRoutes() runs (the user is unauthenticated by
  // definition when they hit /logout) and mounts views/Logout.vue
  // directly as the root component. Adding it here would be a no-op.
  {
    label: 'Dashboards',
    path: 'view',
    icon: 'fa fa-th-large',
    open: true,
    components: {
      default: containers.View,
      header: containers.ViewHeader
    },
    children: [
      {
        label: 'Summary',
        path: 'summary',
        icon: 'icon-speedometer',
        component: dashboards.Summary,
        meta: { hasCharts: true }
      },
      {
        label: 'Airfare',
        path: 'airfare',
        icon: 'icon-plane',
        component: dashboards.Airfare,
        meta: { hasCharts: true }
      },
      {
        label: 'Accommodation',
        path: 'accommodation',
        icon: 'icon-home',
        component: dashboards.Accommodation,
        meta: { hasCharts: true }
      },
      {
        label: 'Rental Car',
        path: 'rentalcar',
        icon: 'fa fa-car',
        component: dashboards.RentalCar,
        meta: { hasCharts: true }
      },
      {
        label: 'Service Fees',
        path: 'servicefees',
        icon: 'fa fa-usd',
        component: dashboards.ServiceFees,
        meta: { hasCharts: true }
      },
      {
        label: 'Traveller',
        path: 'traveller',
        icon: 'icon-user',
        component: dashboards.Traveller,
        meta: { hasCharts: true }
      },
      {
        // Renamed from "Traveller Tracker" → "Travel Tracker" in
        // May 2026 when the Forward Bookings dashboard was
        // retired and its time-window departure filters folded
        // in here. The route name itself stays "Travel Tracker"
        // (set by itemToRoute() from `label`) — call sites that
        // navigate via `name: 'Traveller Tracker'` need updating.
        // URL path kept as `travellertracker` so existing
        // bookmarks resolve without a redirect; new shorter
        // path could be a separate cleanup.
        label: 'Travel Tracker',
        path: 'travellertracker',
        icon: 'fa fa-map-marker',
        component: features.TravellerTracker,
        // Date picker removed from the navbar in May 2026 — TT
        // now owns its own in-page DatePicker (next to the
        // window pills) so the dashboard's date range can't be
        // silently overridden by a navbar selection that's also
        // shared with every other view.
        header: ['hierarchy', 'location'],
        test: hasPerm('traveller_tracker')
      },
      // Tickets in Credit - removed (unused feature)
      {
        label: 'Budgets',
        path: 'budgets',
        icon: 'fa fa-usd',
        component: features.Budgets,
        test: hasPerm('view_budgets'),
        header: ['hierarchy']
      },
      // Travel Agent Reports — TA-Admin-only yield analytics
      // dashboard introduced May 2026. Multi-tab (Branches,
      // Customers, Suppliers, Travel Consultants, Travellers)
      // showing booking-level performance per dimension. Sits
      // after Budgets in the Dashboards sidebar per the design
      // call. The backend endpoint family is at
      // /api/agent_reports/<dimension>/.
      //
      // TEMPORARY (May-2026 release week): gated behind the
      // MK3 shell so travel agents preview it via `?ui=mk3`
      // before the broader rollout. `useMk3Shell` is read from
      // the UI store at menu-build time (sticky per session
      // via localStorage). When MK3 becomes the default OR the
      // preview is complete, drop the `&& useUiStore...` clause
      // — the perm gate alone is the long-term policy.
      {
        label: 'Travel Agent Reports',
        path: 'travelagentreports',
        icon: 'fa fa-line-chart',
        component: features.AgentReports,
        test: user => hasPerm('view_travel_agent_reports')(user)
          && useUiStore().useMk3Shell,
      },
      // Forward Bookings retired May 2026 — time-window departure
      // filters now live on Travel Tracker as a pill row. Legacy
      // /view/forwardbookings URL redirected via the catch-all
      // below so existing bookmarks keep resolving.
      // Benchmarking - removed (unused feature)
    ]
  },
  {
    label: 'Reports',
    path: 'feature',
    icon: 'fa fa-file-text',
    components: {
      default: containers.View,
      header: containers.ViewHeader
    },
    children: [
      {
        label: 'Download Reports',
        path: 'downloadreports',
        icon: 'fa fa-file-text',
        component: dashboards.DownloadReports
      },
      {
        label: 'Scheduled Reports',
        path: 'scheduledreports',
        icon: 'fa fa-file-text',
        component: dashboards.ScheduledReports,
        header: ['hierarchy'],
        test: hasPerm('scheduled_reports')
      },
      {
        label: 'Report History',
        path: 'reporthistory',
        icon: 'fa fa-history',
        component: dashboards.ReportHistory,
        header: ['hierarchy'],
        test: hasPerm('scheduled_reports')
      }
    ]
  },
  {
    label: 'Preferred Suppliers',
    path: 'preferred-suppliers',
    icon: 'fa fa-star',
    header: false,
    // Setup/management of negotiated airline deals — visible to platform
    // admins and to TMC/agent users with supplier/settings rights.
    // Customers see their own contract performance on the Airfare dashboard
    // rather than this admin screen; row-level security scopes the data on
    // both surfaces.
    test: hasAnyPermOrStaff('can_merge_suppliers', 'can_manage_settings'),
    components: {
      default: containers.View,
      header: containers.ViewHeader
    },
    children: [
      {
        label: 'Airlines',
        path: 'airlines',
        icon: 'fa fa-plane',
        component: admin.PreferredAirlines,
        header: ['hierarchy'],
        test: hasAnyPermOrStaff('can_merge_suppliers', 'can_manage_settings'),
      },
      {
        label: 'Hotels',
        path: 'hotels',
        icon: 'fa fa-bed',
        component: admin.PreferredHotels,
        header: ['hierarchy'],
        test: hasAnyPermOrStaff('can_merge_suppliers', 'can_manage_settings'),
      },
    ]
  },
  {
    label: 'Admin',
    path: 'config',
    icon: 'fa fa-cogs',
    header: false,
    test: hasAnyPerm(
      'can_manage_settings',
      'can_manage_tree',
      'can_manage_users',
      'can_edit_policy_codes',
      'can_edit_travellers',
      'can_edit_service_fee_type',
      'can_edit_bookings',
    ),
    components: {
      default: containers.View,
      header: containers.ViewHeader
    },
    // Admin restructured May 2026 — five tabbed sections replace
    // the previous Settings + Report Hierarchy + User Management
    // + Data Editing (4-child) layout. Legacy URLs (/config/
    // settings, /editing/*, etc.) still resolve via the redirect
    // table in getRoutes() below.
    children: [
      {
        label: 'Data Management',
        path: 'data',
        icon: 'fa fa-pencil-square-o',
        component: admin.DataManagement,
        header: ['hierarchy'],
        test: hasAnyPerm(
          'can_edit_policy_codes',
          'can_edit_travellers',
          'can_edit_service_fee_type',
          'can_edit_bookings',
        ),
      },
      {
        label: 'Supplier Management',
        path: 'suppliers',
        icon: 'fa fa-truck',
        component: admin.SupplierManagement,
        // Gated by `can_merge_suppliers` (the canonical post-
        // refactor permission held by TA Admin / TA Standard /
        // Platform Admin) OR the legacy `can_manage_settings`
        // for back-compat during the audit_user_roles rollout
        // window. Pre-rollout, existing users only hold the
        // legacy permission via direct user_permissions —
        // requiring just `can_merge_suppliers` would hide the
        // sidebar entry until every user is migrated into a
        // role-Group. Once rollout completes (per
        // ROLE_ROLLOUT.md), the legacy fallback can be removed.
        //
        // The Airfare Categories sub-tab inside the page has
        // its own additional `is_staff` gate (Platform Admin only).
        test: hasAnyPerm('can_merge_suppliers', 'can_manage_settings'),
      },
      {
        label: 'TMC Details',
        path: 'tmc',
        icon: 'fa fa-id-card-o',
        component: admin.TmcDetails,
        test: hasPerm('can_manage_settings'),
      },
      {
        label: 'Customer Settings',
        path: 'customers',
        icon: 'fa fa-building-o',
        component: admin.CustomerSettings,
        test: hasAnyPerm('can_manage_settings', 'can_manage_tree'),
      },
      {
        label: 'Platform Users',
        path: 'users',
        icon: 'fa fa-users',
        component: admin.PlatformUsers,
        test: hasPerm('can_manage_users'),
      },
    ]
  },
  // In-app Help — a single top-level route (no children) so it renders
  // as a standalone link at the bottom of the sidebar rather than a
  // collapsible section. `header: false` hides the filter bar (a guide
  // needs no date/hierarchy/country filters). No `test` — the page is
  // visible to everyone; the TMC-only Admin Guide is gated as a *tab*
  // inside Help.vue (via user_type), keeping this a single nav entry.
  {
    label: 'Help',
    path: 'help',
    icon: 'fa fa-question-circle',
    component: help.Help,
    header: false,
  },
]

/**
 * Returns a function that yields true if the user has the permission
 * @param {String} permission
 */
function hasPerm (permission) {
  return user => (user && user.permissions && user.permissions.indexOf(permission) !== -1)
}

/**
 * Returns a function that yields true if the user has any of the permissions
 * @param {Array<String>} permissions
 */
function hasAnyPerm (...permissions) {
  return user => (user && user.permissions && user.permissions.some((val) => (permissions.indexOf(val) !== -1)))
}

/**
 * Returns a function that yields true for platform admins (is_staff) OR any
 * user holding one of the listed permissions. Platform admins should always
 * reach admin tooling even if a specific codename isn't on their role.
 */
function hasAnyPermOrStaff (...permissions) {
  return user => Boolean(user && user.is_staff) || hasAnyPerm(...permissions)(user)
}

/**
 * Recursive function used by getRoutes to convert items to route configs
 * @param {Object} user
 * @param {Object} item
 * @param {Object} parent
 */
function itemToRoute (user, item, parent: Record<string, any> = {}) {
  let test = item.test;
  if (test !== undefined) {
    if (typeof test === 'function') {
      test = test(user);
    }
    if (!test) return undefined;
  }
  const route: Record<string, any> = {
    name: item.name || item.label,
    path: ((parent.path) ? '' : '/') + item.path
  };

  ['name', 'component', 'components', 'redirect', 'props', 'meta'].forEach(key => {
    if (Object.prototype.hasOwnProperty.call(item, key)) { route[key] = item[key] }
  })

  // handle header and breacrumbs directives
  const meta = { ...route.meta }
  const headerConfig = item.header !== undefined ? item.header : parent.header
  if (headerConfig !== undefined) {
    if ([null, false].indexOf(headerConfig) !== -1) {
      meta.header = {hide: true}
    } else {
      const hide = ['datepicker', 'hierarchy', 'location']
      meta.header = {hide: hide.filter(comp => headerConfig.indexOf(comp) === -1)}
    }
  }
  const breadcrumbConfig = item.breadcrumb !== undefined
    ? item.breadcrumb : parent.breadcrumb
  if (breadcrumbConfig !== undefined) {
    meta.breadcrumb = breadcrumbConfig
  }
  route.meta = meta

  if (item.children && item.children.length) {
    route.children = item.children.map(
      child => itemToRoute(user, child, item)
    ).filter(Boolean)
  }
  return route;
}

/**
 * Recursive function used by getMenu to convert items to what the Menu code needs
 * @param {Object} user
 * @param {Object} item
 * @param {Object} parent
 */
function itemToMenu (user, item, parent: Record<string, any> = {}) {
  if (item.label === undefined) return undefined;
  let test = item.test;
  if (test !== undefined) {
    if (typeof test === 'function') {
      test = test(user);
    }
    if (!test) return undefined;
  }
  const menu: Record<string, any> = {
    url: (parent.url || '') + '/' + item.path
  };
  ['label', 'icon', 'open'].forEach(key => {
    if (Object.prototype.hasOwnProperty.call(item, key)) { menu[key] = item[key] }
  })
  if (item.children && item.children.length) {
    menu.children = item.children.map(
      child => itemToMenu(user, child, menu)
    ).filter(Boolean)
  }
  return menu;
}

/**
 * Convert the Menu structure into routes for the vue-router.
 * @param {Object} user
 */
export function getRoutes (user) {
  const routes = items.map(item => itemToRoute(user, item)).filter(Boolean)

  // Legacy URL redirects (May-2026 cleanup). Saved bookmarks for
  // retired sidebar entries keep resolving by bouncing to the
  // equivalent new page. Function-form redirects preserve query
  // params — important because Budgets links to
  // `/config/settings?organisation=Foo` to deep-link a specific
  // org. The catch-all `*` redirect at the bottom is the final
  // fallback for anything not enumerated here.
  //
  // Two sources of legacy paths:
  //   * Admin restructure       (Settings / Hierarchy / Editing
  //                              top-level + children → new
  //                              tabbed Admin wrappers)
  //   * Travel Tracker absorbing Forward Bookings (FB dashboard
  //                              retired; its URL → TT)
  const toRoute = (name: string) => (from: any) => ({ name, query: from.query })
  const legacyRedirects: Array<{ path: string, redirect: any }> = [
    // Old Settings tabs → split across the new wrappers
    { path: '/config/settings',                redirect: toRoute('Customer Settings') },
    // Old Report Hierarchy top-level → now a tab under Customer Settings
    { path: '/config/hierarchy',               redirect: toRoute('Customer Settings') },
    // Data Editing top-level + children → Data Management tabs
    { path: '/editing',                        redirect: toRoute('Data Management') },
    { path: '/editing/travelpolicies',         redirect: toRoute('Data Management') },
    { path: '/editing/travellermerging',       redirect: toRoute('Data Management') },
    { path: '/editing/servicefeetypemerging',  redirect: toRoute('Data Management') },
    { path: '/editing/bookings',               redirect: toRoute('Data Management') },
    // Forward Bookings dashboard retired — folded into Travel Tracker
    { path: '/view/forwardbookings',           redirect: toRoute('Travel Tracker') },
  ]
  routes.push(...legacyRedirects)

  routes.push({
    path: '/:pathMatch(.*)*',
    redirect: { name: 'home' }
  })
  return routes
}

/**
 * Convert the Menu structure into something consumable by the Navigation component.
 * @param {Object} user
 */
export function getMenu (user) {
  return items.map(item => itemToMenu(user, item)).filter(Boolean)
}
